import { useT } from "@transifex/react";
import type { SchoolClass } from "p6m-p6u";
import { useState } from "react";
import { useHistory } from "react-router-dom";
import { CreatePopup } from "../../basic/popups/CreatePopup";
import { getSchoolClassesRoute } from "../../helpers/navigationHelper";
import useBreakpoints, { Breakpoint } from "../../hooks/useBreakpoints";
import { ClassActivityCardsOverview } from "../classActivityCard/ClassActivityCardsOverview";
import { AreaButton, ButtonColorVariant, ButtonVariant } from "../teacherArea/AreaButton";
import { Headline } from "../teacherArea/Headline";
import { ClassActivityFilterBar, SortByOptions } from "./ClassActivityFilterBar";

interface AssistantContentSectionProps {
    isLoggedIn: boolean;
    isOnboardingComplete: boolean;
    schoolClasses: SchoolClass[];
    totalActivityCount: number;
}

export const AssistantContentSection = (props: AssistantContentSectionProps) => {
    const history = useHistory();
    const t = useT();
    const { getStyleBasedOnBreakpoint } = useBreakpoints();

    const [classFilter, setClassFilter] = useState("");
    const [languageFilter, setLanguageFilter] = useState("");
    const [sortBy, setSortBy] = useState<SortByOptions>(SortByOptions.CREATED_DESC);
    const [availableLanguages, setAvailableLanguages] = useState<string[]>([]);
    const [isCreatePopupOpen, setIsCreatePopupOpen] = useState(false);

    const t_assistantTitle = t("The Teacher Assistant", { _tags: "TeacherArea" });
    const t_manageClasses = t("Manage classes", { _tags: "TeacherArea" });
    const t_createNew = t("Create new", { _tags: "TeacherArea" });

    const buttonWidthClass = getStyleBasedOnBreakpoint(Breakpoint.MD, "", "w-full");

    return (
        <>
            {props.isLoggedIn && (
                <div className="flex flex-col w-full items-center gap-m">
                    {props.totalActivityCount > 0 && props.isOnboardingComplete && (
                        <Headline
                            level={1}
                            className="text-xxl font-semibold text-p6-grey-dark pb-m"
                        >
                            {t_assistantTitle}
                        </Headline>
                    )}
                    <div
                        className={`flex w-full gap-s ${getStyleBasedOnBreakpoint(
                            Breakpoint.MD,
                            "flex-row justify-between",
                            "flex-col"
                        )}`}
                    >
                        <AreaButton
                            variant={ButtonVariant.SECONDARY}
                            colorVariant={ButtonColorVariant.DEFAULT_WHITE}
                            role="button"
                            className={buttonWidthClass}
                            onClick={() => history.push(getSchoolClassesRoute())}
                        >
                            {t_manageClasses}
                        </AreaButton>
                        <AreaButton
                            data-testid="button-create-new"
                            variant={ButtonVariant.PRIMARY}
                            role="button"
                            iconName="add"
                            className={buttonWidthClass}
                            onClick={() => setIsCreatePopupOpen(true)}
                        >
                            {t_createNew}
                        </AreaButton>
                    </div>
                </div>
            )}

            <div className="flex flex-col gap-s w-full">
                {props.totalActivityCount > 0 && (
                    <ClassActivityFilterBar
                        classFilter={classFilter}
                        onClassFilterChange={setClassFilter}
                        availableClasses={props.schoolClasses}
                        languageFilter={languageFilter}
                        onLanguageFilterChange={setLanguageFilter}
                        sortBy={sortBy}
                        onSortByChange={setSortBy}
                        availableLanguages={availableLanguages}
                    />
                )}
                <ClassActivityCardsOverview
                    classFilter={classFilter}
                    languageFilter={languageFilter}
                    sortBy={sortBy}
                    onAvailableLanguagesChange={setAvailableLanguages}
                />
            </div>

            <CreatePopup
                isOpen={isCreatePopupOpen}
                onClose={() => setIsCreatePopupOpen(false)}
            />
        </>
    );
};
